import React, { useEffect, useState, useRef } from 'react';
import { Play, Pause, SkipBack, SkipForward, Volume2, VolumeX } from 'lucide-react';
interface CustomAudioPlayerProps {
  audioUrl: string;
}
export function CustomAudioPlayer({
  audioUrl
}: CustomAudioPlayerProps) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const updateTime = () => setCurrentTime(audio.currentTime);
    const updateDuration = () => setDuration(audio.duration);
    const handleEnded = () => setIsPlaying(false);
    audio.addEventListener('timeupdate', updateTime);
    audio.addEventListener('loadedmetadata', updateDuration);
    audio.addEventListener('ended', handleEnded);
    return () => {
      audio.removeEventListener('timeupdate', updateTime);
      audio.removeEventListener('loadedmetadata', updateDuration);
      audio.removeEventListener('ended', handleEnded);
    };
  }, []);
  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };
  const handleProgressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTime = parseFloat(e.target.value);
    setCurrentTime(newTime);
    if (audioRef.current) {
      audioRef.current.currentTime = newTime;
    }
  };
  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (audioRef.current) {
      audioRef.current.volume = newVolume;
    }
    setIsMuted(newVolume === 0);
  };
  const toggleMute = () => {
    if (audioRef.current) {
      if (isMuted) {
        audioRef.current.volume = volume;
        setIsMuted(false);
      } else {
        audioRef.current.volume = 0;
        setIsMuted(true);
      }
    }
  };
  const skip = (seconds: number) => {
    if (audioRef.current) {
      audioRef.current.currentTime += seconds;
    }
  };
  const formatTime = (time: number) => {
    if (isNaN(time)) return '0:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };
  return <div className="w-full max-w-4xl mx-auto" data-id="element-44">
      <audio ref={audioRef} src={audioUrl} preload="metadata" data-id="element-45" />

      <div className="bg-[#FBF9F6]/95 backdrop-blur-md rounded-2xl p-6 shadow-soft-lg border border-[#E8905C]/20" data-id="element-46">
        {/* Progress Bar */}
        <div className="mb-4" data-id="element-47">
          <input type="range" min="0" max={duration || 0} value={currentTime} onChange={handleProgressChange} className="w-full h-2 bg-[#E8DAC7] rounded-full appearance-none cursor-pointer" style={{
          background: `linear-gradient(to right, #E8905C 0%, #E8905C ${currentTime / duration * 100}%, #E8DAC7 ${currentTime / duration * 100}%, #E8DAC7 100%)`
        }} data-id="element-48" />
          <div className="flex justify-between mt-2 text-xs text-[#3E4A55]" data-id="element-49">
            <span data-id="element-50">{formatTime(currentTime)}</span>
            <span data-id="element-51">{formatTime(duration)}</span>
          </div>
        </div>

        {/* Controls */}
        <div className="flex items-center justify-between" data-id="element-52">
          <div className="flex items-center gap-3" data-id="element-53">
            {/* Skip Back */}
            <button onClick={() => skip(-10)} className="p-2 rounded-full hover:bg-[#E8DAC7] transition-colors" aria-label="Retroceder 10 segundos" data-id="element-54">
              <SkipBack className="w-5 h-5 text-[#3E4A55]" data-id="element-55" />
            </button>

            {/* Play/Pause */}
            <button onClick={togglePlay} className="p-3 rounded-full bg-[#C48A47] hover:bg-[#915E2D] transition-colors shadow-soft" aria-label={isPlaying ? 'Pausar' : 'Reproducir'} data-id="element-56">
              {isPlaying ? <Pause className="w-6 h-6 text-white" fill="white" data-id="element-57" /> : <Play className="w-6 h-6 text-white" fill="white" data-id="element-58" />}
            </button>

            {/* Skip Forward */}
            <button onClick={() => skip(10)} className="p-2 rounded-full hover:bg-[#E8DAC7] transition-colors" aria-label="Adelantar 10 segundos" data-id="element-59">
              <SkipForward className="w-5 h-5 text-[#3E4A55]" data-id="element-60" />
            </button>
          </div>

          {/* Volume Control */}
          <div className="flex items-center gap-2" data-id="element-61">
            <button onClick={toggleMute} className="p-2 rounded-full hover:bg-[#E8DAC7] transition-colors" aria-label={isMuted ? 'Activar sonido' : 'Silenciar'} data-id="element-62">
              {isMuted ? <VolumeX className="w-5 h-5 text-[#3E4A55]" data-id="element-63" /> : <Volume2 className="w-5 h-5 text-[#3E4A55]" data-id="element-64" />}
            </button>
            <input type="range" min="0" max="1" step="0.01" value={isMuted ? 0 : volume} onChange={handleVolumeChange} className="w-20 h-2 bg-[#E8DAC7] rounded-full appearance-none cursor-pointer" style={{
            background: `linear-gradient(to right, #E8905C 0%, #E8905C ${(isMuted ? 0 : volume) * 100}%, #E8DAC7 ${(isMuted ? 0 : volume) * 100}%, #E8DAC7 100%)`
          }} data-id="element-65" />
          </div>
        </div>
      </div>
    </div>;
}